import { useEffect, useState } from "react";
import { Handshake, Loader2, Save, Check, X, Ban, Target, Euro, Users, RotateCcw, Play } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fcfa = (n) => `${Math.round(Number(n || 0)).toLocaleString("fr-FR")} FCFA`;
const eur = (n) => `${Number(n || 0).toFixed(2)} €`;
const CORRIDORS = { tg_ga: "Togo → Gabon", ga_fr: "Gabon → France", ga_rw: "Gabon → Rwanda", fr_ga: "France → Gabon" };
const PARTNER_BADGE = {
  pending: "bg-amber-50 text-amber-700", approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700", suspended: "bg-gray-100 text-gray-600",
};
const COMM_BADGE = { pending: "bg-amber-50 text-amber-700", validated: "bg-sky-50 text-sky-700", paid: "bg-emerald-50 text-emerald-700" };

export default function AdminPartners() {
  const [tab, setTab] = useState("program");
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [partners, setPartners] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadCore = async () => {
    const [c, s] = await Promise.all([api.get("/admin/partner/config"), api.get("/admin/partner/stats")]);
    setCfg(c.data); setStats(s.data);
  };

  const refresh = async () => {
    setLoading(true);
    try { await loadCore(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    if (tab === "partners") api.get("/admin/partners").then((r) => setPartners(r.data || [])).catch(() => {});
    if (tab === "commissions") api.get("/admin/partner/commissions").then((r) => setCommissions(r.data || [])).catch(() => {});
    if (tab === "campaigns") api.get("/admin/partner/campaigns").then((r) => setCampaigns(r.data || [])).catch(() => {});
  }, [tab]);

  const saveCfg = async () => {
    setSaving(true);
    try {
      const payload = {
        title: cfg.title, status: cfg.status, accepting_applications: cfg.accepting_applications,
        closed_message: cfg.closed_message, commission_rate: Number(cfg.commission_rate),
        eligible_corridors: cfg.eligible_corridors, min_amount: Number(cfg.min_amount),
        cap_per_tx: Number(cfg.cap_per_tx), objective_amount: Number(cfg.objective_amount),
        objective_period: cfg.objective_period, conditions: cfg.conditions,
      };
      const r = await api.patch("/admin/partner/config", payload);
      setCfg(r.data); await loadCore();
      toast.success("Programme enregistré");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const decidePartner = async (id, decision) => {
    let reason = null;
    if (decision === "reject") { reason = window.prompt("Motif du refus :", ""); if (reason === null) return; }
    try {
      await api.post(`/admin/partners/${id}/decision`, { decision, reason });
      toast.success("Mis à jour");
      const r = await api.get("/admin/partners"); setPartners(r.data || []); await loadCore();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const setCommStatus = async (id, status) => {
    try {
      await api.post(`/admin/partner/commissions/${id}/status`, { status });
      toast.success("Commission mise à jour");
      const r = await api.get("/admin/partner/commissions"); setCommissions(r.data || []); await loadCore();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const resetCampaign = async () => {
    if (!window.confirm("Archiver la campagne actuelle et démarrer une nouvelle période ? Le volume réalisé sera remis à zéro.")) return;
    try {
      await api.post("/admin/partner/campaign/reset");
      toast.success("Nouvelle campagne démarrée");
      await loadCore();
      if (tab === "campaigns") { const r = await api.get("/admin/partner/campaigns"); setCampaigns(r.data || []); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const toggleCorridor = (c) => {
    const cur = cfg.eligible_corridors || [];
    setCfg({ ...cfg, eligible_corridors: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] });
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-[var(--sl-primary)]" /></div>;

  return (
    <div data-testid="admin-partners-page">
      <div className="flex items-center gap-3 mb-6">
        <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><Handshake size={18} /></span>
        <div>
          <h1 className="font-heading text-2xl font-bold">Partenaire engagé</h1>
          <p className="text-sm text-gray-500">Pilotez le programme, les partenaires et les commissions.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {[["program", "Programme"], ["partners", "Partenaires"], ["commissions", "Commissions"], ["campaigns", "Campagnes"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`ptab-${k}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition ${tab === k ? "bg-[var(--sl-primary)] text-white" : "bg-white border text-gray-600 hover:bg-gray-50"}`}>{l}</button>
        ))}
      </div>

      {/* PROGRAM TAB */}
      {tab === "program" && cfg && stats && (
        <div className="space-y-5" data-testid="partner-program-tab">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Target} label="Réalisé" value={fcfa(stats.realized_xaf)} testid="pstat-realized" />
            <Stat icon={Target} label="Objectif" value={fcfa(stats.objective_amount)} testid="pstat-objective" />
            <Stat icon={Users} label="Partenaires actifs" value={stats.partners_count?.approved || 0} testid="pstat-approved" />
            <Stat icon={Euro} label="Com. à payer" value={eur((stats.commission_totals?.pending || 0) + (stats.commission_totals?.validated || 0))} testid="pstat-topay" />
          </div>

          <div className="rounded-2xl border bg-white p-5">
            <div className="flex justify-between text-sm mb-1">
              <span>Progression ({stats.objective_period})</span><span>{stats.progress_percent}%</span>
            </div>
            <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-[var(--sl-primary)]" style={{ width: `${stats.progress_percent}%` }} />
            </div>
            <button onClick={resetCampaign} className="mt-3 text-xs inline-flex items-center gap-1 text-[var(--sl-primary)] hover:underline" data-testid="reset-campaign">
              <RotateCcw size={13} /> Archiver & démarrer une nouvelle campagne
            </button>
          </div>

          <div className="rounded-2xl border bg-white p-5 space-y-4 max-w-2xl">
            <h3 className="font-semibold">Règles & conditions</h3>
            <label className="flex items-center justify-between">
              <span className="text-sm font-medium">Statut du programme</span>
              <select value={cfg.status} onChange={(e) => setCfg({ ...cfg, status: e.target.value })} className="rounded-xl border px-3 py-1.5 text-sm" data-testid="pcfg-status">
                <option value="open">Ouvert</option><option value="suspended">Suspendu</option><option value="closed">Fermé</option>
              </select>
            </label>
            <label className="flex items-center justify-between cursor-pointer">
              <span className="text-sm font-medium">Accepter de nouvelles candidatures</span>
              <input type="checkbox" checked={!!cfg.accepting_applications} onChange={(e) => setCfg({ ...cfg, accepting_applications: e.target.checked })} className="w-5 h-5 accent-[var(--sl-primary)]" data-testid="pcfg-accepting" />
            </label>
            <Field label="Taux de commission (%)" value={cfg.commission_rate} onChange={(v) => setCfg({ ...cfg, commission_rate: v })} testid="pcfg-rate" />
            <Field label="Objectif de volume (FCFA)" value={cfg.objective_amount} onChange={(v) => setCfg({ ...cfg, objective_amount: v })} testid="pcfg-objective" />
            <label className="block">
              <span className="text-sm text-gray-600">Durée de l'objectif</span>
              <select value={cfg.objective_period} onChange={(e) => setCfg({ ...cfg, objective_period: e.target.value })} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm" data-testid="pcfg-period">
                <option value="day">Journée</option><option value="week">Semaine</option><option value="month">Mois</option>
              </select>
            </label>
            <Field label="Montant minimum éligible (devise source)" value={cfg.min_amount} onChange={(v) => setCfg({ ...cfg, min_amount: v })} testid="pcfg-min" />
            <Field label="Plafond de commission / transaction (0 = aucun)" value={cfg.cap_per_tx} onChange={(v) => setCfg({ ...cfg, cap_per_tx: v })} testid="pcfg-cap" />
            <div>
              <span className="text-sm text-gray-600">Corridors éligibles</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(CORRIDORS).map(([c, l]) => (
                  <button key={c} onClick={() => toggleCorridor(c)} data-testid={`pcfg-corridor-${c}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border ${(cfg.eligible_corridors || []).includes(c) ? "bg-[var(--sl-primary)] text-white border-transparent" : "bg-white text-gray-600"}`}>{l}</button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-sm text-gray-600">Conditions (texte public)</span>
              <textarea value={cfg.conditions || ""} onChange={(e) => setCfg({ ...cfg, conditions: e.target.value })} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm min-h-[70px]" data-testid="pcfg-conditions" />
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Message « programme fermé / complet »</span>
              <textarea value={cfg.closed_message || ""} onChange={(e) => setCfg({ ...cfg, closed_message: e.target.value })} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm min-h-[60px]" data-testid="pcfg-closed-msg" />
            </label>
            <button onClick={saveCfg} disabled={saving} className="w-full rounded-xl bg-[var(--sl-primary)] text-white py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="pcfg-save">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Enregistrer le programme
            </button>
          </div>
        </div>
      )}

      {/* PARTNERS TAB */}
      {tab === "partners" && (
        <div className="space-y-3" data-testid="partner-list-tab">
          {partners.length === 0 && <div className="sl-card text-center text-gray-400 py-10">Aucune candidature.</div>}
          {partners.map((p) => (
            <div key={p.id} className="rounded-2xl border bg-white p-4 flex flex-wrap items-center gap-3" data-testid={`partner-${p.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold">{p.name || p.email} {p.code && <span className="font-mono text-xs text-gray-400">· {p.code}</span>}</div>
                <div className="text-xs text-gray-400">{p.email} · {p.phone}</div>
                {p.audience && <div className="text-xs text-gray-500 mt-1">Audience : {p.audience}</div>}
                {p.motivation && <div className="text-xs text-gray-500">« {p.motivation} »</div>}
                <div className="text-xs text-gray-500 mt-1">{p.stats?.tx_count || 0} tx · {fcfa(p.stats?.volume_xaf)} · {eur((p.stats?.commission_pending || 0) + (p.stats?.commission_validated || 0) + (p.stats?.commission_paid || 0))} com.</div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${PARTNER_BADGE[p.status] || "bg-gray-100"}`}>{p.status}</span>
              <div className="flex gap-2">
                {p.status === "pending" && (
                  <>
                    <button onClick={() => decidePartner(p.id, "accept")} className="text-xs inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5" data-testid={`partner-accept-${p.id}`}><Check size={12} /> Accepter</button>
                    <button onClick={() => decidePartner(p.id, "reject")} className="text-xs inline-flex items-center gap-1 rounded-full border border-rose-200 text-rose-600 px-3 py-1.5" data-testid={`partner-reject-${p.id}`}><X size={12} /> Refuser</button>
                  </>
                )}
                {p.status === "approved" && (
                  <button onClick={() => decidePartner(p.id, "suspend")} className="text-xs inline-flex items-center gap-1 rounded-full border border-gray-200 text-gray-600 px-3 py-1.5" data-testid={`partner-suspend-${p.id}`}><Ban size={12} /> Suspendre</button>
                )}
                {(p.status === "suspended" || p.status === "rejected") && (
                  <button onClick={() => decidePartner(p.id, "reactivate")} className="text-xs inline-flex items-center gap-1 rounded-full bg-[var(--sl-primary)] text-white px-3 py-1.5" data-testid={`partner-reactivate-${p.id}`}><Play size={12} /> Réactiver</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* COMMISSIONS TAB */}
      {tab === "commissions" && (
        <div className="rounded-2xl border bg-white overflow-hidden" data-testid="commissions-tab">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr><th className="px-4 py-3">Date</th><th className="px-4 py-3">Transfert</th><th className="px-4 py-3">Partenaire</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {commissions.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Aucune commission.</td></tr>}
              {commissions.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`commission-${c.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">{c.tx_code}<div className="text-xs text-gray-400">{fcfa(c.amount_xaf)}</div></td>
                  <td className="px-4 py-3 font-mono text-xs">{c.partner_code}</td>
                  <td className="px-4 py-3 font-semibold">{eur(c.commission_amount)}<div className="text-xs text-gray-400 font-normal">{c.rate_percent}%</div></td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${COMM_BADGE[c.status] || "bg-gray-100"}`}>{c.status}</span></td>
                  <td className="px-4 py-3 text-right">
                    {c.status === "pending" && <button onClick={() => setCommStatus(c.id, "validated")} className="text-xs text-sky-600 font-medium" data-testid={`comm-validate-${c.id}`}>Valider</button>}
                    {c.status === "validated" && <button onClick={() => setCommStatus(c.id, "paid")} className="text-xs text-emerald-600 font-medium" data-testid={`comm-pay-${c.id}`}>Marquer payée</button>}
                    {c.status === "paid" && <span className="text-xs text-gray-300">✓ payée</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CAMPAIGNS TAB */}
      {tab === "campaigns" && (
        <div className="space-y-3" data-testid="campaigns-tab">
          {campaigns.length === 0 && <div className="sl-card text-center text-gray-400 py-10">Aucune campagne archivée.</div>}
          {campaigns.map((c) => (
            <div key={c.id} className="rounded-2xl border bg-white p-4" data-testid={`campaign-${c.id}`}>
              <div className="flex justify-between">
                <div className="font-semibold">{fcfa(c.realized_xaf)} / {fcfa(c.objective_amount)} <span className="text-xs text-gray-400">({c.objective_period})</span></div>
                <div className="text-xs text-gray-400">{(c.period_start || "").slice(0, 10)} → {(c.period_end || "").slice(0, 10)}</div>
              </div>
              <div className="text-xs text-gray-500 mt-1">Commission {c.commission_rate}% · archivée par {c.archived_by}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-gray-400 mb-1"><Icon size={15} /><span className="text-xs">{label}</span></div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-sm text-gray-600">{label}</span>
      <input type="number" step="0.01" min="0" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-1 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--sl-primary)]/30" />
    </label>
  );
}
